/**
 * Dice expression parser and roller using crypto.getRandomValues for fairness.
 *
 * Supports standard D&D dice notation:
 *   - NdX       → roll N dice with X faces (e.g. 2d6)
 *   - dX        → shorthand for 1dX (e.g. d20)
 *   - NdX+M     → roll + flat modifier (e.g. 1d20+5)
 *   - NdX-M     → roll - flat modifier (e.g. 2d8-1)
 *   - Multiple groups: 2d6+1d4+3  (additive only)
 *
 * The parser is intentionally simple — no parentheses, no multiplication, no
 * "keep highest N". Those can be added later if the table needs them.
 */

/** A single parsed dice group: NdX or a flat modifier. */
export interface DiceGroup {
  count: number;
  faces: number;
}

export interface ParsedExpression {
  groups: DiceGroup[];
  modifier: number;
}

/**
 * Parse a dice expression string into structured groups + modifier.
 * Throws on invalid input.
 *
 * Examples:
 *   "2d6+3"    → groups: [{count:2, faces:6}], modifier: 3
 *   "1d20+1d4" → groups: [{count:1, faces:20}, {count:1, faces:4}], modifier: 0
 *   "d8-1"     → groups: [{count:1, faces:8}], modifier: -1
 *   "3d6"      → groups: [{count:3, faces:6}], modifier: 0
 */
export function parseDiceExpression(input: string): ParsedExpression {
  const expr = input.replace(/\s+/g, '').toLowerCase();
  if (!expr) throw new Error('Empty dice expression');

  const groups: DiceGroup[] = [];
  let modifier = 0;

  // Split on + and - while keeping the sign as a delimiter.
  // "2d6+3-1d4+2" → ["2d6", "+3", "-1d4", "+2"]
  const tokens = expr.match(/[+-]?[^+-]+/g);
  if (!tokens) throw new Error(`Invalid dice expression: ${input}`);

  for (const token of tokens) {
    const diceMatch = token.match(/^([+-]?)(\d*)d(\d+)$/);
    if (diceMatch) {
      const sign = diceMatch[1] === '-' ? -1 : 1;
      const count = diceMatch[2] ? parseInt(diceMatch[2], 10) : 1;
      const faces = parseInt(diceMatch[3], 10);
      if (count <= 0 || count > 100) throw new Error(`Die count out of range: ${count}`);
      if (faces <= 0 || faces > 1000) throw new Error(`Die faces out of range: ${faces}`);
      // Negative dice groups (e.g. -1d4) aren't standard D&D notation but are
      // technically parseable; for now we only support positive groups.
      if (sign < 0) throw new Error('Negative dice groups are not supported');
      groups.push({ count, faces });
      continue;
    }

    const modMatch = token.match(/^([+-]?\d+)$/);
    if (modMatch) {
      modifier += parseInt(modMatch[1], 10);
      continue;
    }

    throw new Error(`Unrecognised token in dice expression: ${token}`);
  }

  if (groups.length === 0) {
    throw new Error(`No dice in expression: ${input}`);
  }

  return { groups, modifier };
}

/**
 * Roll a single die with the given number of faces using crypto.getRandomValues.
 * Returns a value in [1, faces].
 */
function rollOne(faces: number): number {
  // Use rejection sampling to avoid modulo bias. We generate a 32-bit
  // unsigned integer and reject values that fall in the biased tail.
  const max = Math.floor(0x100000000 / faces) * faces;
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0] < max) {
      return (buf[0] % faces) + 1;
    }
  }
}

export interface RollResult {
  /** The original expression string. */
  expression: string;
  /** Individual die results in roll order (does not include modifier). */
  results: number[];
  /** Sum of all dice + modifier. */
  total: number;
  /** Parsed modifier (0 if none). */
  modifier: number;
}

/**
 * Parse an expression, roll all the dice, and return the full result.
 */
export function rollDice(expression: string): RollResult {
  const parsed = parseDiceExpression(expression);
  const results: number[] = [];

  for (const group of parsed.groups) {
    for (let i = 0; i < group.count; i++) {
      results.push(rollOne(group.faces));
    }
  }

  const diceSum = results.reduce((a, b) => a + b, 0);
  return {
    expression,
    results,
    total: diceSum + parsed.modifier,
    modifier: parsed.modifier,
  };
}

/**
 * Format a roll result into a human-readable string.
 * e.g. "2d6+3 → [4, 2] +3 = 9"
 */
export function formatRoll(roll: RollResult): string {
  const dice = `[${roll.results.join(', ')}]`;
  const mod = roll.modifier !== 0
    ? ` ${roll.modifier > 0 ? '+' : ''}${roll.modifier}`
    : '';
  return `${roll.expression} → ${dice}${mod} = ${roll.total}`;
}
